import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import {
  BarChart3, TrendingUp, Users, Target, Globe, Activity, ArrowUpRight,
  ChevronDown, Calendar, Zap
} from "lucide-react";

const fmtInt = (n) => (Number(n) || 0).toLocaleString("es-ES");
const pct = (a, b) => (b > 0 ? Math.round((a / b) * 100) : 0);

export default function Analytics() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/analytics?days=${days}`).then((r) => { setData(r.data); setLoading(false); });
  }, [days]);

  if (loading || !data) return <div className="text-slate-500">Cargando analítica…</div>;

  const maxTs = Math.max(1, ...data.timeseries.map((t) => t.total));

  return (
    <div data-testid="admin-analytics">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="kbar flex items-center gap-2"><BarChart3 className="w-3 h-3" /> Analítica propia · sin cookies</div>
          <h1 className="font-display font-extrabold text-3xl sm:text-5xl tracking-tight mt-2">Dashboard</h1>
          <p className="text-slate-500 mt-2">
            Últimos {days} días · datos de los eventos guardados en tu propia base de datos.
            <Link to="/admin" className="text-orange-600 hover:underline ml-2">← Volver al panel admin</Link>
          </p>
        </div>
        <div className="flex items-center gap-1 border border-slate-200 bg-white" data-testid="period-selector">
          {[7, 30, 90, 365].map((d) => (
            <button
              key={d}
              data-testid={`period-${d}`}
              onClick={() => setDays(d)}
              className={`h-10 px-4 text-sm font-bold ${days === d ? "bg-slate-900 text-white" : "hover:bg-slate-50"}`}
            >{d}d</button>
          ))}
        </div>
      </div>

      {/* Totals */}
      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
        <Stat icon={Globe} label="Sesiones únicas" value={fmtInt(data.totals.unique_sessions)} testid="stat-sessions" />
        <Stat icon={Target} label="Leads capturados" value={fmtInt(data.totals.leads)} testid="stat-leads" />
        <Stat icon={Users} label="Usuarios registrados" value={fmtInt(data.totals.registered_users)} testid="stat-users" accent />
        <Stat icon={Zap} label="Facturas emitidas" value={fmtInt(data.totals.invoices_issued)} sub={`${fmtInt(data.totals.budgets)} presupuestos`} testid="stat-invoices" />
      </div>

      {/* Funnel */}
      <div className="mt-10 bg-white border border-slate-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="kbar">Embudo de conversión</div>
            <h2 className="font-display font-extrabold text-xl mt-1">Del tráfico al primer cobro</h2>
          </div>
          <span className="text-xs text-slate-400">Periodo: {data.start}</span>
        </div>
        <div className="mt-6 space-y-2" data-testid="funnel">
          {data.funnel.map((f, i) => {
            const first = data.funnel[0].count || 1;
            const prev = i === 0 ? f.count : (data.funnel[i - 1].count || 1);
            const widthPct = Math.max(4, pct(f.count, first));
            const dropFromPrev = i === 0 ? 100 : pct(f.count, prev);
            return (
              <div key={f.step} className="flex items-center gap-4" data-testid={`funnel-step-${i}`}>
                <div className="w-56 flex-shrink-0">
                  <div className="text-sm font-bold">{f.step}</div>
                  <div className="text-xs text-slate-500">{i > 0 && `${dropFromPrev}% del paso anterior · `}{pct(f.count, first)}% del total</div>
                </div>
                <div className="flex-1 bg-slate-100 h-10 relative overflow-hidden">
                  <div
                    className="h-full flex items-center justify-end pr-3 text-white font-bold text-sm tabular-nums transition-all"
                    style={{ width: `${widthPct}%`, background: `linear-gradient(90deg, #EA580C ${Math.max(0, 100 - i * 18)}%, #c2410c)` }}
                  >
                    {fmtInt(f.count)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Timeseries */}
      <div className="mt-10 bg-white border border-slate-200 p-6">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="kbar">Actividad por día</div>
            <h2 className="font-display font-extrabold text-xl mt-1">Tráfico y eventos</h2>
          </div>
          <div className="flex items-center gap-3 text-xs text-slate-500 flex-wrap">
            <LegendDot color="#0f172a" label="Visitas" />
            <LegendDot color="#EA580C" label="Leads" />
            <LegendDot color="#16a34a" label="Registros" />
            <LegendDot color="#7c3aed" label="1ª factura" />
          </div>
        </div>
        <div className="mt-6 h-64 flex items-end gap-1" data-testid="timeseries">
          {data.timeseries.map((t) => (
            <div key={t.day} className="flex-1 flex flex-col items-center gap-0.5 group relative" title={`${t.day} · ${t.total} eventos`}>
              <div className="w-full flex items-end gap-[1px] h-[90%]">
                <div className="w-1/4" style={{ background: "#0f172a", height: `${(t.page_view / maxTs) * 100}%`, minHeight: t.page_view ? "2px" : 0 }} />
                <div className="w-1/4" style={{ background: "#EA580C", height: `${(t.lead_submit_success / maxTs) * 100}%`, minHeight: t.lead_submit_success ? "2px" : 0 }} />
                <div className="w-1/4" style={{ background: "#16a34a", height: `${(t.register_completed / maxTs) * 100}%`, minHeight: t.register_completed ? "2px" : 0 }} />
                <div className="w-1/4" style={{ background: "#7c3aed", height: `${(t.first_invoice_issued / maxTs) * 100}%`, minHeight: t.first_invoice_issued ? "2px" : 0 }} />
              </div>
              <div className="text-[9px] text-slate-400 font-mono">{t.day.slice(8)}</div>
              {/* Hover tooltip */}
              <div className="hidden group-hover:block absolute -top-24 left-1/2 -translate-x-1/2 bg-slate-900 text-white text-[10px] whitespace-nowrap px-2 py-1.5 shadow-lg z-10">
                <div className="font-bold">{t.day}</div>
                <div>Visitas: {t.page_view}</div>
                <div>Leads: {t.lead_submit_success}</div>
                <div>Registros: {t.register_completed}</div>
                <div>1ª factura: {t.first_invoice_issued}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Sources + Trades */}
      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
        <div className="bg-white p-6" data-testid="traffic-sources">
          <div className="kbar mb-4 inline-flex items-center gap-2"><Globe className="w-3 h-3" /> Orígenes de tráfico</div>
          {data.sources.length === 0 ? (
            <div className="text-sm text-slate-400 py-8 text-center">Sin datos aún. Usa ?utm_source=... en tus enlaces.</div>
          ) : (
            <div className="space-y-2">
              {data.sources.map((s) => {
                const max = data.sources[0].count || 1;
                return (
                  <div key={s.source} className="flex items-center gap-3">
                    <div className="w-24 text-sm font-mono truncate">{s.source}</div>
                    <div className="flex-1 bg-slate-100 h-6 relative">
                      <div className="h-full bg-slate-900" style={{ width: `${(s.count / max) * 100}%` }} />
                    </div>
                    <div className="w-10 text-right font-bold tabular-nums text-sm">{s.count}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="bg-white p-6" data-testid="top-trades">
          <div className="kbar mb-4 inline-flex items-center gap-2"><Target className="w-3 h-3" /> Oficios más clicados</div>
          {data.top_trades.length === 0 ? (
            <div className="text-sm text-slate-400 py-8 text-center">Aún ningún clic sobre oficios.</div>
          ) : (
            <div className="space-y-2">
              {data.top_trades.map((t) => {
                const max = data.top_trades[0].count || 1;
                return (
                  <div key={t.trade} className="flex items-center gap-3">
                    <div className="w-24 text-sm font-semibold capitalize">{t.trade}</div>
                    <div className="flex-1 bg-slate-100 h-6 relative">
                      <div className="h-full bg-orange-600" style={{ width: `${(t.count / max) * 100}%` }} />
                    </div>
                    <div className="w-10 text-right font-bold tabular-nums text-sm">{t.count}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* All events */}
      <div className="mt-10 bg-white border border-slate-200 p-6">
        <div className="kbar mb-4 inline-flex items-center gap-2"><Activity className="w-3 h-3" /> Todos los eventos ({fmtInt(data.totals.events)})</div>
        {Object.keys(data.events_by_name).length === 0 ? (
          <div className="text-sm text-slate-400 py-4">Sin eventos en este periodo.</div>
        ) : (
          <table className="w-full text-sm" data-testid="events-table">
            <thead className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <tr>
                <th className="text-left py-2">Evento</th>
                <th className="text-right py-2 w-32">Cantidad</th>
                <th className="text-right py-2 w-20">%</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(data.events_by_name).map(([name, count]) => (
                <tr key={name} className="border-b border-slate-100">
                  <td className="py-2 font-mono">{name}</td>
                  <td className="py-2 text-right tabular-nums font-bold">{fmtInt(count)}</td>
                  <td className="py-2 text-right tabular-nums text-slate-500">{pct(count, data.totals.events)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, sub, accent, testid }) {
  return (
    <div data-testid={testid} className={`bg-white p-6 relative ${accent ? "" : ""}`}>
      {accent && <div className="absolute top-0 left-0 right-0 h-1 bg-orange-600" />}
      <div className="flex items-center justify-between">
        <span className="kbar">{label}</span>
        <Icon className="w-4 h-4 text-slate-400" />
      </div>
      <div className="font-display font-extrabold text-3xl sm:text-4xl mt-2 tabular-nums">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

function LegendDot({ color, label }) {
  return <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3" style={{ background: color }} /> {label}</span>;
}
